import { useCallback, useEffect, useLayoutEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react'
import clsx from 'clsx'
import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { getComponentHeights } from '../utils'
import { ChartIcicleBody } from './ChartIcicleBody'
import type { ChartIcicleDataSchema, ChartIcicleOrientation, ChartIcicleUISchema } from './types'

const MIN_CANVAS_SCALE = 0.5
const MAX_CANVAS_SCALE = 3
const DRAG_THRESHOLD_PX = 5
const CANVAS_INSET = 16

interface CanvasTransform {
  x: number
  y: number
  scale: number
}

export function ChartIcicleContent({
  uiSchema,
  dataSchema,
  tileFilters,
}: {
  uiSchema: ChartIcicleUISchema
  dataSchema: ChartIcicleDataSchema
  tileFilters?: React.ReactNode
}) {
  const { title = undefined, description = undefined, emphasizeHeader } = uiSchema
  const chartConfig = uiSchema.chartConfig ?? { orientation: 'horizontal' as const, config: {} }
  const hasHeader = !!title && !!tileFilters
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)
  const enablePanZoom = chartConfig.enablePanZoom !== false
  const nodes = Array.isArray(dataSchema.data) ? dataSchema.data : []
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() => new Set())

  const toggleCollapsed = useCallback((id: string) => {
    setCollapsedIds((current) => {
      const next = new Set(current)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const body = (isClickSuppressed?: () => boolean) => (
    <ChartIcicleBody
      nodes={nodes}
      chartConfig={chartConfig}
      collapsedIds={collapsedIds}
      onToggleCollapsed={toggleCollapsed}
      isClickSuppressed={isClickSuppressed}
    />
  )

  if (nodes.length === 0) {
    return (
      <div className="flex h-full flex-col">
        {hasHeader && (
          <ComponentHeader emphasize={emphasizeHeader}>
            <ComponentTitle title={title} description={description} />
            <ComponentActionsContainer>{tileFilters}</ComponentActionsContainer>
          </ComponentHeader>
        )}
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">No data available</div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      {hasHeader && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <ComponentActionsContainer>{tileFilters}</ComponentActionsContainer>
        </ComponentHeader>
      )}
      {/* <div className={clsx('flex min-h-0 flex-1 flex-col', { 'pt-5': hasHeader })} style={{ height: contentHeight }}> */}
      <div className={clsx('flex min-h-0 flex-1 flex-col')} style={{ height: contentHeight }}>
        {enablePanZoom && (
          // <div className="flex shrink-0 items-center justify-end border-b border-border px-3 py-2">
          <div className="flex shrink-0 items-center justify-end px-3 py-2">
            <span className="hidden text-xs text-muted-foreground sm:inline">Drag to pan · Scroll to zoom</span>
          </div>
        )}
        {enablePanZoom ? (
          <IciclePanZoomCanvas orientation={chartConfig.orientation}>{body}</IciclePanZoomCanvas>
        ) : (
          <div className="min-h-0 flex-1 overflow-auto p-4">
            <div className="h-full w-max min-w-full">{body()}</div>
          </div>
        )}
      </div>
    </div>
  )
}

function IciclePanZoomCanvas({
  children,
  orientation,
}: {
  children: (isClickSuppressed: () => boolean) => ReactNode
  orientation: ChartIcicleOrientation
}) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null)
  const didDragRef = useRef(false)
  const transformRef = useRef<CanvasTransform>({ x: CANVAS_INSET, y: CANVAS_INSET, scale: 1 })
  const [transform, setTransform] = useState<CanvasTransform>(transformRef.current)
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 })
  transformRef.current = transform

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) {
      return
    }
    const update = () => {
      setViewportSize({ width: viewport.clientWidth, height: viewport.clientHeight })
    }
    update()
    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver(update)
    observer.observe(viewport)
    return () => observer.disconnect()
  }, [])

  const zoomBy = useCallback((factor: number, centerX?: number, centerY?: number) => {
    setTransform((current) => {
      const scale = Math.min(MAX_CANVAS_SCALE, Math.max(MIN_CANVAS_SCALE, current.scale * factor))
      const ratio = scale / current.scale
      const viewport = viewportRef.current
      const x = centerX ?? (viewport?.clientWidth ?? 0) / 2
      const y = centerY ?? (viewport?.clientHeight ?? 0) / 2
      return {
        scale,
        x: x - (x - current.x) * ratio,
        y: y - (y - current.y) * ratio,
      }
    })
  }, [])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) {
      return
    }
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault()
      const bounds = viewport.getBoundingClientRect()
      zoomBy(Math.exp(-event.deltaY * 0.0015), event.clientX - bounds.left, event.clientY - bounds.top)
    }
    viewport.addEventListener('wheel', handleWheel, { passive: false })
    return () => viewport.removeEventListener('wheel', handleWheel)
  }, [zoomBy])

  const onPointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return
    }
    const current = transformRef.current
    didDragRef.current = false
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: current.x,
      originY: current.y,
    }
  }, [])

  const onPointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag) {
      return
    }
    const dx = event.clientX - drag.startX
    const dy = event.clientY - drag.startY
    if (!didDragRef.current) {
      if (dx * dx + dy * dy < DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) {
        return
      }
      didDragRef.current = true
      event.currentTarget.setPointerCapture(event.pointerId)
    }
    setTransform({
      scale: transformRef.current.scale,
      x: drag.originX + dx,
      y: drag.originY + dy,
    })
  }, [])

  const onPointerUp = useCallback((event: PointerEvent<HTMLDivElement>) => {
    dragRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    queueMicrotask(() => {
      didDragRef.current = false
    })
  }, [])

  const isClickSuppressed = useCallback(() => didDragRef.current, [])
  const isHorizontal = orientation === 'horizontal'
  const canvasHeight = Math.max(0, viewportSize.height - CANVAS_INSET * 2)
  const canvasWidth = Math.max(0, viewportSize.width - CANVAS_INSET * 2)

  return (
    <div
      ref={viewportRef}
      className="relative min-h-0 min-w-0 flex-1 cursor-grab touch-none select-none overflow-hidden bg-muted/10 active:cursor-grabbing"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      aria-label="Icicle chart canvas. Drag to pan and scroll to zoom."
    >
      <div
        className="absolute left-0 top-0 origin-top-left"
        style={{
          height: isHorizontal ? canvasHeight || undefined : undefined,
          width: isHorizontal ? undefined : canvasWidth || undefined,
          transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
        }}
      >
        {children(isClickSuppressed)}
      </div>
    </div>
  )
}
